import type { RelativeDateRange } from 'components/datepicker/types';
import type { HourColumn, SupportedExtensions, TaskColumn } from 'constants/export';
import type {
  ClientDataEntity,
  CommonDataEntity,
  DayDataEntity,
  LinkedWorkDataEntity,
  ProjectDataEntity,
  ReportQuery,
  TagDataEntity,
  TeamDataEntity,
  UserDataEntity,
  WidgetEntity,
} from 'redux/modules/superreports.types';
import type { CostAndDurationSummary } from 'utils/summary';
import type { DateTypePeriodNames } from './dates';
import type { LightUser } from './user';

export interface ReportBase {
  description: string;
  events?: boolean;
  exportFields: Array<HourColumn>;
  taskExportFields: Array<TaskColumn>;
  filters?: ReportQuery['filters'];
  id: number;
  live?: boolean;
  name: string;
  widgets?: Array<WidgetEntity>;
  downloaded_reports: ReportDownloadHistory;
}

export interface DownloadedReportFile {
  id: number;
  type: SupportedExtensions;
  url: string;
  downloaded_at: string;
  user: LightUser;
}

type ReportDownloadHistory = Array<DownloadedReportFile>;
interface ReportEntityBaseFixedDates extends ReportBase {
  from: string;
  to: string;
}

interface ReportEntityBaseRelativeDates extends ReportBase {
  date_range: RelativeDateRange;
}

/**
 * Response from `/shared_reports.json` and `/shared_reports/xx`.
 */
export interface PublicReportEntity
  extends ReportEntityBaseFixedDates,
    ReportEntityBaseRelativeDates {
  name: string;
  created_at: string;
  events: boolean;
  hours: number;
  live: boolean;
  minutes: number;
  public_url: string;
  report_id: number | null;
  seen_at: string;
}

/**
 * Response from `/reports.json` and `/reports/1.json`.
 */
export interface ReportEntity<T extends Array<string> | string = Array<string>>
  extends ReportEntityBaseFixedDates,
    ReportEntityBaseRelativeDates {
  include_hours: boolean;
  span: DateTypePeriodNames;
  updated_at: string;
  user: Pick<LightUser, 'id' | 'email' | 'name' | 'avatar'> & {
    updated_at: string;
  };
  widget_types: Array<string>;
  hour_fields?: T;
  task_fields?: T;
  pinned?: boolean;
}

export interface ReportFilter {
  direction?: 'asc' | 'desc';
  limit?: number;
  order?: 'asc' | 'desc';
  offset: number;
  sort_by: string;
  user_ids: Array<number>;
}

export type SharedReportFilterType = 'live' | 'snapshot';

export interface SharedReportsFilter {
  userIds: Array<number>;
  from: string;
  to: string;
  limit: number;
  offset: number;
  order: 'asc' | 'desc';
  report_type: SharedReportFilterType;
  sort_by: string;
  span: DateTypePeriodNames;
}

/**
 * Response from `/reports/widgets`.
 */
export interface WidgetMetadata {
  columns: {
    names: Array<string>;
    order: Array<string>;
    sort_by: Array<string>;
  };
  display: string;
  group_by: EntityDataKey;
  options: {};
  title: string;
  widget_type: string;
}

/**
 * Keys used for the `group_by` parameter of `/reports/filter.json`.
 */
export enum EntityDataKey {
  ClientsAndProjects = 'clients',
  Days = 'days',
  Tags = 'labels',
  Tasks = 'tasks',
  Teams = 'teams',
  Users = 'users',
  LinkedWork = 'linked_work',
  Totals = 'totals',
}

/**
 * Query that can be passed to a request to `/reports/filter.json`.
 */
export interface EntityDataQuery {
  /**
   * Comma-separated list of tag IDs. If set, only hours matching these tag IDs will be
   * included in the response.
   */
  label_ids?: string;
  limit?: number;
  offset?: number;
  order?: 'asc' | 'desc';
  page?: number;

  /**
   * Comma-separated list of project IDs or special string values ('all', 'active', 'archived').
   */
  project_ids?: string;
  published?: boolean;

  /**
   * If set to `events`, the response will be an array of hours instead of an `EntityDataResponse`
   * instance.
   */
  scope?: 'events';

  /**
   * Earliest date to include events for, in yyyy-MM-dd format.
   * @example 2019-04-13
   */
  since?: string;

  sort?: string;

  /**
   * Latest date to include events for, in yyyy-MM-dd format.
   * @example 2020-08-17
   */
  until?: string;

  /**
   * Relative date range like 'this_week', 'this_month', 'prev_week', 'prev_month', 'this_quarter', 'prev_quarter', 'this_year', 'prev_year'
   * @example 2024-08-26
   */
  date_range?: string;

  /**
   * Comma-separated list of user IDs. If set, only hours matching these user IDs will be
   * included in the response.
   */
  user_ids?: string;

  /**
   * Filter for external links. Values: 'with_links', 'without_links', 'provider:jira', 'provider:clickup', etc.
   */
  external_link_filter?: string;

  /**
   * Filter for sync status. Values: 'synced', 'pending', 'failed', 'auth_required', 'needs_attention', 'with_status', 'without_status'
   */
  sync_status_filter?: string;

  // To get LightProject instead of Project payloads
  projects_version?: 3;
  // If true, exclude totals when grouping results by entity
  group_only?: boolean;
}

/**
 * Response from `/reports/filter.json`.
 */
export interface EntityDataResponse {
  clients: Array<ClientDataEntity<ProjectDataEntity>>;
  days: Array<DayDataEntity>;
  labels: Array<TagDataEntity>;
  linked_work: Array<LinkedWorkDataEntity>;
  multiple_currency_report: boolean;
  teams: Array<TeamDataEntity>;
  totals: CommonDataEntity;
  users: Array<UserDataEntity>;
}

export type EntityDataWithTransformedTotals = EntityDataResponse & {
  totals: CostAndDurationSummary;
};
